import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Share2, BarChart3, Check, X, Trophy, Percent } from 'lucide-react';
import { useWallet } from '@solana/wallet-adapter-react';
import { getBaseUrl, blinksApi } from '../services/blinksApi';

interface DropTier {
  name: string;
  chance: number;
  multiplier: number;
  color: string;
}

const BlinkPreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { publicKey, connected } = useWallet();
  const affiliate = searchParams.get('affiliate');
  const [copied, setCopied] = useState(false);
  const [showOdds, setShowOdds] = useState(false);
  const [dropTable, setDropTable] = useState<DropTier[]>([]);
  const [crateName, setCrateName] = useState<string>('');

  // Fetch crate config for odds
  useEffect(() => {
    const fetchCrateConfig = async () => {
      if (!id) return;
      try {
        const config = await blinksApi.getCrateConfig(id);
        if (config && config.tiers) {
          setCrateName(config.name || 'Crate');
          const tiers: DropTier[] = config.tiers.map((tier: any) => ({
            name: tier.name,
            chance: tier.chance,
            multiplier: tier.multiplier,
            color: tier.color || getTierColor(tier.name)
          }));
          setDropTable(tiers);
        }
      } catch (error) {
        console.error('Error fetching crate config:', error);
      }
    };
    fetchCrateConfig();
  }, [id]);

  const getTierColor = (name: string): string => {
    const colors: Record<string, string> = {
      'Common': '#888888',
      'Uncommon': '#00FF9C',
      'Rare': '#00BFFF',
      'Epic': '#A855F7',
      'Legendary': '#FFD700',
      'Mythic': '#FF4500',
      'Jackpot': '#FF00FF'
    };
    return colors[name] || '#888888';
  };

  if (!id) {
    return (
      <div style={{ 
        padding: '100px', 
        textAlign: 'center', 
        color: '#fff',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <h2>Crate not found</h2>
        <button 
          onClick={() => navigate('/blinks')} 
          style={{ 
            marginTop: '20px',
            padding: '12px 24px',
            background: 'transparent',
            border: '1px solid #fff',
            color: '#fff',
            cursor: 'pointer'
          }}
        >
          Back to Crates
        </button>
      </div>
    );
  }

  const apiBaseUrl = getBaseUrl(process.env.REACT_APP_API_URL || 'http://localhost:3001');
  const previewUrl = `${apiBaseUrl}/api/blinks/preview/${id}${affiliate ? `?affiliate=${affiliate}` : ''}`;
  
  // Build share URL with affiliate parameter
  const affiliateParam = connected && publicKey ? publicKey.toString() : affiliate;
  const shareUrl = `${apiBaseUrl}/api/blinks/action/${id}${affiliateParam ? `?affiliate=${affiliateParam}` : ''}`;

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(0, 0, 0, 0.8)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '0.9rem',
    padding: '12px 20px',
    borderRadius: '8px',
    transition: 'all 0.2s',
    backdropFilter: 'blur(10px)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontFamily: 'var(--font-display)'
  };

  return (
    <div style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Background matching site */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `url(${require('../assets/animesky.png')})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        zIndex: -1
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.7)'
        }} />
      </div>
      {/* Floating toolbar */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        right: '20px',
        zIndex: 1001,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pointerEvents: 'none'
      }}>
        {/* Left side - Back button */}
        <button 
          onClick={() => navigate(-1)}
          style={{ ...buttonStyle, pointerEvents: 'auto' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
          }}
        >
          <ArrowLeft size={18} /> Back
        </button>

        {/* Right side - Share and Odds buttons */}
        <div style={{ display: 'flex', gap: '12px', pointerEvents: 'auto' }}>
          <button 
            onClick={() => setShowOdds(true)}
            style={buttonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <BarChart3 size={18} /> Odds
          </button>
          
          <button 
            onClick={handleShare}
            style={{
              ...buttonStyle,
              background: copied ? 'rgba(0, 255, 156, 0.2)' : 'rgba(0, 0, 0, 0.8)',
              borderColor: copied ? 'rgba(0, 255, 156, 0.6)' : 'rgba(255, 255, 255, 0.2)'
            }}
            onMouseEnter={(e) => {
              if (!copied) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!copied) {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              }
            }}
          >
            {copied ? <Check size={18} /> : <Share2 size={18} />}
            {copied ? 'Copied!' : 'Share'}
          </button>
        </div>
      </div>

      {/* Odds Modal */}
      {showOdds && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            zIndex: 1002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowOdds(false)}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, rgba(20, 20, 30, 0.98), rgba(10, 10, 20, 0.98))',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ color: '#fff', margin: 0, fontSize: '1.5rem' }}>
                {crateName ? `${crateName} Odds` : 'Drop Rates & Odds'}
              </h2>
              <button
                onClick={() => setShowOdds(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {dropTable.length > 0 ? (
              <>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr auto auto', 
                  gap: '12px',
                  marginBottom: '8px',
                  padding: '0 8px',
                  color: '#888',
                  fontSize: '0.85rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  <span>Tier</span>
                  <span>Chance</span>
                  <span>Payout</span>
                </div>

                {dropTable.map((tier, index) => (
                  <div 
                    key={index}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto auto',
                      gap: '12px',
                      padding: '12px 8px',
                      borderLeft: `3px solid ${tier.color}`,
                      background: 'rgba(255, 255, 255, 0.02)',
                      marginBottom: '8px',
                      borderRadius: '0 8px 8px 0'
                    }}
                  >
                    <span style={{ color: tier.color, fontWeight: 'bold' }}>{tier.name}</span>
                    <span style={{ color: '#fff', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Percent size={14} color="#888" />
                      {tier.chance.toFixed(1)}%
                    </span>
                    <span style={{ color: '#00FF9C', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Trophy size={14} color="#888" />
                      {tier.multiplier}x
                    </span>
                  </div>
                ))}

                {/* Visualization bar */}
                <div style={{ 
                  display: 'flex', 
                  height: '8px', 
                  borderRadius: '4px', 
                  overflow: 'hidden',
                  marginTop: '24px'
                }}>
                  {dropTable.map((tier, index) => (
                    <div
                      key={index}
                      style={{
                        width: `${tier.chance}%`,
                        background: tier.color,
                        minWidth: tier.chance > 0 ? '2px' : '0'
                      }}
                      title={`${tier.name}: ${tier.chance}%`}
                    />
                  ))}
                </div>
                <p style={{ color: '#666', fontSize: '0.8rem', textAlign: 'center', marginTop: '8px' }}>
                  Drop Distribution
                </p>
              </>
            ) : (
              <p style={{ color: '#888', textAlign: 'center' }}>Loading odds...</p>
            )}

            <div style={{
              marginTop: '24px',
              padding: '16px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <BarChart3 size={20} color="#888" style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ color: '#888', fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>
                All outcomes are determined on-chain using a commit-reveal scheme. 
                Any roll not hitting a tier results in a loss (0x).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Full-screen iframe */}
      <iframe
        src={previewUrl}
        style={{
          flex: 1,
          width: '100%',
          height: '100%',
          border: 'none',
          background: 'transparent'
        }}
        title="Crate Preview"
        allow="clipboard-write"
      />
    </div>
  );
};

export default BlinkPreview;
